<template>
  <div class="main-content">
    <!-- Filtros -->
    <div class="card p-3 mb-3">
      <div class="row">
        <div class="col-md-3 mt-2">
          <label>Agência</label>
          <v-select
            v-model="filters.central_id"
            :options="centrals"
            :reduce="(c) => c.id"
            label="fantasy_name"
            multiple
            placeholder="Todas"
            @input="onCentralChange"
          />
        </div>
        <div class="col-md-3 mt-2">
          <label>Serviço</label>
          <v-select
            v-model="filters.occurrence_type"
            :options="[{ value: 'APH', label: 'APH' }]"
            :reduce="(o) => o.value"
            label="label"
            placeholder="Todos"
          />
        </div>
        <div class="col-md-3 mt-2">
          <label>Data Inicial</label>
          <input
            type="date"
            class="form-control"
            v-model="filters.start_date"
            :max="filters.end_date || null"
          />
        </div>
        <div class="col-md-3 mt-2">
          <label>Data Final</label>
          <input
            type="date"
            class="form-control"
            v-model="filters.end_date"
            :min="filters.start_date || null"
          />
        </div>
        <div class="col-md-3 mt-2">
          <label>Convênio</label>
          <v-select
            v-model="filters.health_plan"
            :options="plans"
            :reduce="(p) => p.id"
            label="title"
            placeholder="Todos"
          />
        </div>
        <div class="col-md-4 mt-2">
          <label>Unidade</label>
          <v-select
            v-model="filters.unit_id"
            :options="units"
            :reduce="(u) => u.id"
            label="unit_name"
            multiple
            placeholder="Todas"
          />
        </div>
        <div class="col-md-3 mt-2">
          <label>Tipo de Desfecho</label>
          <v-select
            v-model="filters.outcome_status"
            :options="outcomeOptions"
            placeholder="Todos"
          />
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 d-flex flex-wrap" style="gap: 8px">
          <button class="btn btn-primary" @click="onFiltrar">
            Filtrar
          </button>
          <button
            class="btn btn-success"
            @click="exportToExcel"
            :disabled="exporting"
          >
            {{ exporting ? "Gerando…" : "Exportar Relatório" }}
          </button>
          <button
            class="btn btn-outline-success"
            @click="exportOutcomeTemplate"
          >
            Exportar Modelo (Desfecho)
          </button>
          <button class="btn btn-outline-primary" @click="triggerOutcomeImport">
            Importar Desfechos
          </button>
          <input
            ref="outcomeFile"
            type="file"
            accept=".xlsx,.xls,.csv"
            style="display: none"
            @change="importOutcomes"
          />
        </div>
      </div>
    </div>
    <!-- Resultado da importação de desfechos -->
    <div v-if="importResult" class="card p-2 mb-2">
      <div
        class="alert mb-2"
        :class="importResult.com_erro > 0 ? 'alert-warning' : 'alert-success'"
      >
        <strong>Importação concluída.</strong>
        Atualizados: <strong>{{ importResult.atualizados }}</strong> · Ignorados
        (em branco): <strong>{{ importResult.ignorados || 0 }}</strong> · Com
        erro: <strong>{{ importResult.com_erro }}</strong>
        <button type="button" class="close" @click="importResult = null">
          &times;
        </button>
      </div>

      <div class="row">
        <!-- Importados -->
        <div
          v-if="importResult.importados && importResult.importados.length"
          class="col-md-6"
        >
          <small class="text-success d-block mb-1">
            <strong>Importados ({{ importResult.importados.length }}):</strong>
          </small>
          <ul class="mb-0" style="max-height: 220px; overflow-y: auto">
            <li v-for="(oc, i) in importResult.importados" :key="'imp' + i">
              <small
                >OC {{ oc.external_id || oc.id
                }}{{ oc.name ? " — " + oc.name : "" }}</small
              >
            </li>
          </ul>
        </div>

        <!-- Inconsistências -->
        <div
          v-if="importResult.erros && importResult.erros.length"
          class="col-md-6"
        >
          <small class="text-danger d-block mb-1">
            <strong>Inconsistências ({{ importResult.erros.length }}):</strong>
          </small>
          <ul class="mb-0" style="max-height: 220px; overflow-y: auto">
            <li v-for="(erro, i) in importResult.erros" :key="'err' + i">
              <small>{{ erro }}</small>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Tabela -->
    <div class="card p-1">
      <div class="table-responsive">
        <table class="table table-bordered table-hover table-sm">
          <thead>
            <tr>
              <th>Data da Ocorrência</th>
              <th>Tipo</th>
              <th>Motivo</th>
              <th>Bairro</th>
              <th>Município</th>
              <th>Unidade</th>
              <th>Convênio</th>
              <th>Horário de Abertura</th>
              <th>Saída do Local</th>
              <th>Chegada na Unidade</th>
              <th>Saída da Unidade</th>
              <th>Internação</th>
              <th>UTI</th>
              <th>Cirurgia</th>
              <th>CID</th>
              <th>Desfecho</th>
              <th>Observação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="occurrences.length === 0">
              <td colspan="17" class="text-center text-muted py-3">
                Nenhum registro encontrado.
              </td>
            </tr>
            <tr v-for="o in occurrences" :key="o.id">
              <td>{{ o.opening_date_h || "—" }}</td>
              <td>{{ o.type ? o.type.title : "—" }}</td>
              <td>{{ o.reason ? o.reason.title : "—" }}</td>
              <td>
                {{ o.people ? o.people.neighborhood : o.neighborhood || "—" }}
              </td>
              <td>{{ o.people ? o.people.city || "—" : "—" }}</td>
              <td>{{ o.unit ? o.unit.unit_name : "—" }}</td>
              <td>{{ o.plan ? o.plan.title : "—" }}</td>
              <td>{{ o.opening_date_f || "—" }}</td>
              <td>{{ o.departure_from_unit_f || "—" }}</td>
              <td>{{ o.arrival_at_unit_f || "—" }}</td>
              <td>{{ o.output_unit_f || "—" }}</td>
              <td>
                {{ formatBool(o.outcome ? o.outcome.hospitalization : null) }}
              </td>
              <td>{{ formatBool(o.outcome ? o.outcome.uti : null) }}</td>
              <td>{{ formatBool(o.outcome ? o.outcome.surgery : null) }}</td>
              <td>
                <span v-if="o.outcome && o.outcome.cid_code">
                  {{ o.outcome.cid_code }} — {{ o.outcome.cid_description }}
                </span>
                <span v-else>—</span>
              </td>
              <td>{{ o.outcome ? o.outcome.outcome_status || "—" : "—" }}</td>
              <td style="max-width: 200px; white-space: normal">
                {{ o.outcome ? o.outcome.observation || "—" : "—" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="col-md-6 mt-1 mx-auto d-flex justify-content-center align-items-center"
      >
        <pagination
          :records="total"
          :options="paginationOptions"
          :per-page="15"
          @paginate="fetchReport"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Pagination } from "vue-pagination-2";

export default {
  name: "ReportIndex",
  components: { Pagination },
  data() {
    return {
      filters: {
        central_id: [],
        occurrence_type: "APH",
        start_date: "",
        end_date: "",
        health_plan: null,
        unit_id: [],
        outcome_status: null,
      },
      occurrences: [],
      total: 0,
      centrals: [],
      units: [],
      plans: [],
      importResult: null,
      exporting: false,
      currentExportId: null,
      exportPoll: null,
      outcomeOptions: [
        "Alta – Melhora do quadro",
        "Alta – Revelia",
        "Alta – Ambulatorial",
        "Não Localizado",
        "Óbito",
        "Permanece Internado(a)",
        "Transferência outra unidade",
      ],
      paginationOptions: {
        texts: {
          count: "Mostrando {from} de {to} até {count} de registros",
        },
        chunk: 4,
        chunksNavigation: "fixed",
      },
    };
  },
  methods: {
    // Bloqueia ações quando a Data Final é anterior à Data Inicial.
    // (datas do <input type="date"> vêm como 'YYYY-MM-DD' -> comparação de string funciona)
    datesValid() {
      const ini = this.filters.start_date;
      const fim = this.filters.end_date;
      if (ini && fim && fim < ini) {
        swal(
          "Datas inválidas",
          "A Data Final deve ser maior ou igual à Data Inicial.",
          "warning"
        );
        return false;
      }
      return true;
    },

    onFiltrar() {
      if (!this.datesValid()) return;
      this.fetchReport(1);
    },

    fetchReport(page) {
      const self = this;
      const p = page || 1;
      const api = self.$store.state.api + "reports?page=" + p;

      self.$loading(true);
      axios
        .post(api, self.buildFilters())
        .then((res) => {
          self.occurrences = res.data.data;
          self.total = res.data.total;
        })
        .catch((err) => {
          self.$message(
            null,
            err.response?.data || "Erro ao carregar relatório.",
            "error"
          );
        })
        .finally(() => self.$loading(false));
    },

    buildFilters() {
      const f = {};
      if (this.filters.central_id && this.filters.central_id.length)
        f.central_id = this.filters.central_id;
      if (this.filters.occurrence_type)
        f.occurrence_type = this.filters.occurrence_type;
      if (this.filters.start_date) f.start_date = this.filters.start_date;
      if (this.filters.end_date) f.end_date = this.filters.end_date;
      if (this.filters.health_plan) f.health_plan = this.filters.health_plan;
      if (this.filters.unit_id && this.filters.unit_id.length)
        f.unit_id = this.filters.unit_id;
      if (this.filters.outcome_status)
        f.outcome_status = this.filters.outcome_status;
      return f;
    },

    // Exportação assíncrona: enfileira a geração (não trava o servidor mesmo com
    // centenas de milhares de linhas) e avisa quando o arquivo fica pronto via
    // Reverb (.export.ready), com polling de fallback.
    exportToExcel() {
      if (!this.datesValid()) return;
      const self = this;
      const f = this.buildFilters();
      f.user_id = this.$store.state.user.usuario_id;

      self.exporting = true;
      if (self.$message)
        self.$message(
          "Gerando",
          "Gerando o relatório… você será avisado quando ficar pronto.",
          "info"
        );

      axios
        .post(self.$store.state.api + "exports", { type: "report", filters: f })
        .then((res) => {
          self.currentExportId = res.data.export.id;
          self.startExportPolling(self.currentExportId);
        })
        .catch(() => {
          self.exporting = false;
          if (self.$message)
            self.$message(
              null,
              "Não foi possível iniciar a exportação.",
              "error"
            );
        });
    },

    // Aviso em tempo real (Reverb): dispara quando o job termina.
    onExportReady(e) {
      if (!e || e.id !== this.currentExportId) return;
      if (e.status === "done") this.finishExport(e);
      else this.failExport();
    },

    // Fallback caso o websocket não chegue: consulta o status a cada 5s.
    startExportPolling(id) {
      const self = this;
      this.stopExportPolling();
      this.exportPoll = setInterval(() => {
        axios
          .get(self.$store.state.api + "exports/" + id)
          .then((res) => {
            if (res.data.status === "done") self.finishExport(res.data);
            else if (res.data.status === "failed") self.failExport();
          })
          .catch(() => {});
      }, 5000);
    },

    stopExportPolling() {
      if (this.exportPoll) {
        clearInterval(this.exportPoll);
        this.exportPoll = null;
      }
    },

    // Idempotente: Reverb e polling podem chegar quase juntos.
    finishExport(exp) {
      if (!this.exporting) return;
      this.exporting = false;
      this.currentExportId = null;
      this.stopExportPolling();
      this.downloadReady(exp);
    },

    failExport() {
      if (!this.exporting) return;
      this.exporting = false;
      this.currentExportId = null;
      this.stopExportPolling();
      if (this.$message)
        this.$message(null, "Falha ao gerar o arquivo.", "error");
    },

    // A rota de download é autenticada (JWT) -> baixa via blob, não window.open.
    downloadReady(exp) {
      const self = this;
      axios
        .get(self.$store.state.api + "exports/" + exp.id + "/download", {
          responseType: "blob",
        })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement("a");
          a.href = url;
          a.download = exp.file_name || "relatorio.csv";
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          if (self.$message)
            self.$message("Pronto", "Seu relatório foi baixado.", "success");
        })
        .catch(() => {
          if (self.$message)
            self.$message(
              null,
              "Arquivo pronto, mas o download falhou.",
              "error"
            );
        });
    },

    // Exporta o modelo de desfecho (mesmos filtros da tela) — rota web (GET), igual ao relatório
    exportOutcomeTemplate() {
      if (!this.datesValid()) return;
      const userId = this.$store.state.user.usuario_id;
      const f = this.buildFilters();
      let params = [];

      if (f.central_id) params.push("central_id=" + f.central_id.join(","));
      if (f.occurrence_type)
        params.push("occurrence_type=" + f.occurrence_type);
      if (f.start_date) params.push("start_date=" + f.start_date);
      if (f.end_date) params.push("end_date=" + f.end_date);
      if (f.health_plan) params.push("health_plan=" + f.health_plan);
      if (f.unit_id) params.push("unit_id=" + f.unit_id.join(","));
      if (f.outcome_status)
        params.push("outcome_status=" + encodeURIComponent(f.outcome_status));
      if (userId) params.push("user_id=" + userId);

      const url =
        this.$store.state.url +
        "outcomes/export-template" +
        (params.length ? "?" + params.join("&") : "");
      window.open(url, "_blank").focus();
    },

    triggerOutcomeImport() {
      this.$refs.outcomeFile.click();
    },

    importOutcomes(e) {
      const file = e.target.files[0];
      if (!file) return;

      const self = this;
      const form = new FormData();
      form.append("file", file);

      self.importResult = null;
      if (self.$loading) self.$loading(true);

      axios
        .post(self.$store.state.api + "occurrence-outcomes/import", form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          self.importResult = res.data;
          // Só recarrega a grid se algo foi REALMENTE atualizado. Senão, a grid do
          // relatório (que não tem relação com a planilha) aparecia abaixo do resumo
          // e dava a falsa impressão de que a importação "gerou" esses dados.
          if (res.data && res.data.atualizados > 0) {
            self.fetchReport(1);
          }
        })
        .catch((err) => {
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Erro ao importar a planilha.";
          self.$message(null, msg, "error");
        })
        .finally(() => {
          if (self.$loading) self.$loading(false);
          e.target.value = ""; // permite reimportar o mesmo arquivo
        });
    },

    onCentralChange() {
      this.filters.health_plan = null;
      this.filters.unit_id = [];
      this.loadUnits();
      this.loadPlans();
    },

    loadCentrals() {
      axios
        .get(this.$store.state.api + "centrals?paginated=false")
        .then((res) => {
          this.centrals = (res.data.data || []).sort((a, b) =>
            (a.fantasy_name || "").localeCompare(b.fantasy_name || "")
          );
        });
    },

    loadUnits() {
      let api = this.$store.state.api + "units/filter-by-central";
      if (this.filters.central_id && this.filters.central_id.length) {
        api += "?central_ids=" + this.filters.central_id.join(",");
      }
      axios.get(api).then((res) => {
        this.units = (res.data.data || []).sort((a, b) =>
          a.unit_name.localeCompare(b.unit_name)
        );
      });
    },

    loadPlans() {
      let api = this.$store.state.api + "medical-agreements?paginated=false";
      if (this.filters.central_id && this.filters.central_id.length) {
        api += "&central_id=" + this.filters.central_id.join(",");
      }
      axios.get(api).then((res) => {
        const unique = (res.data.data || []).filter(
          (p, i, arr) => arr.findIndex((x) => x.title === p.title) === i
        );
        this.plans = unique.sort((a, b) => a.title.localeCompare(b.title));
      });
    },

    formatBool(val) {
      if (val === null || val === undefined) return "—";
      return val ? "Sim" : "Não";
    },
  },
  mounted() {
    this.loadCentrals();
    this.loadUnits();
    this.loadPlans();

    const userId = this.$store.state.user && this.$store.state.user.usuario_id;
    if (userId && window.Echo) {
      window.Echo.private(`private-user.${userId}`).listen(
        ".export.ready",
        (e) => this.onExportReady(e)
      );
    }
  },

  beforeDestroy() {
    this.stopExportPolling();
    const userId = this.$store.state.user && this.$store.state.user.usuario_id;
    if (userId && window.Echo) {
      window.Echo.private(`private-user.${userId}`).stopListening(
        ".export.ready"
      );
    }
  },
};
</script>

<style scoped>
.main-content {
  padding: 20px;
}
.table th,
.table td {
  text-align: center;
  vertical-align: middle;
}
</style>
